
import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { toast } from 'react-hot-toast';
import proveedorService, { Proveedor } from '../services/proveedorService';
import {
  PlusIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const ProveedoresPage: React.FC = () => {
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState<string | null>(null);
  const [formData, setFormData] = useState({ nombre: '', codigo_proveedor: '' });

  // States for confirmation modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState<Proveedor | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      setLoading(true);
      const response = await proveedorService.getProveedores();
      setProveedores(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Error al cargar proveedores');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (proveedor?: Proveedor) => {
    if (proveedor) {
      setEditingProveedor(proveedor.nombre);
      setFormData({
        nombre: proveedor.nombre,
        codigo_proveedor: proveedor.codigo_proveedor || ''
      });
    } else {
      setEditingProveedor(null);
      setFormData({ nombre: '', codigo_proveedor: '' });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProveedor(null);
    setFormData({ nombre: '', codigo_proveedor: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre del proveedor es requerido');
      return;
    }

    if (!formData.codigo_proveedor.trim()) {
      toast.error('El código del proveedor es requerido');
      return;
    }

    try {
      if (editingProveedor) {
        await proveedorService.updateProveedor(editingProveedor, formData);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await proveedorService.createProveedor(formData);
        toast.success('Proveedor creado exitosamente');
      }
      handleCloseModal();
      loadProveedores();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar proveedor');
      console.error(error);
    }
  };

  // Open status change confirmation
  const handleToggleStatusClick = (proveedor: Proveedor) => {
    setSelectedProveedor(proveedor);
    setShowStatusModal(true);
  };

  // Execute status change
  const handleConfirmStatusChange = async () => {
    if (!selectedProveedor) return;

    try {
      setStatusLoading(true);
      if (selectedProveedor.estado) {
        // Deactivate
        const response = await proveedorService.deactivateProveedor(selectedProveedor.nombre);
        const count = response.data?.productosDesactivados || 0;
        toast.success(`Proveedor desactivado (${count} productos desactivados)`);
      } else {
        // Activate
        const response = await proveedorService.activateProveedor(selectedProveedor.nombre);
        const count = response.data?.productosActivados || 0;
        toast.success(`Proveedor activado (${count} productos activados)`);
      }

      setShowStatusModal(false);
      setSelectedProveedor(null);
      loadProveedores();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar estado');
      console.error(error);
    } finally {
      setStatusLoading(false);
    }
  };

  const filteredProveedores = proveedores.filter(proveedor =>
    proveedor.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Proveedores</h1>
              <p className="text-gray-600 mt-1">Gestiona los proveedores del inventario</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Nuevo Proveedor
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando proveedores...</p>
            </div>
          ) : filteredProveedores.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre del Proveedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cód. Proveedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Productos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProveedores.map((proveedor) => (
                  <tr key={proveedor.nombre} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{proveedor.nombre}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{proveedor.codigo_proveedor || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{proveedor.totalProductos}</div>
                      <div className="text-xs text-gray-500">{proveedor.productosActivos} activos</div>
                      {proveedor.desglose && (
                        <div className="flex gap-1 mt-1">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title="Inventario Maestro">M: {proveedor.desglose.maestro}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" title="Inventario Novias">N: {proveedor.desglose.novias}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {proveedor.estado ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Activo
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleOpenModal(proveedor)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Editar"
                      >
                        <PencilIcon className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleToggleStatusClick(proveedor)}
                        className={proveedor.estado ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                        title={proveedor.estado ? 'Desactivar' : 'Activar'}
                      >
                        {proveedor.estado ? (
                          <NoSymbolIcon className="w-5 h-5 inline" />
                        ) : (
                          <CheckCircleIcon className="w-5 h-5 inline" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Stats */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">
            Total de proveedores: <span className="font-bold text-gray-900">{filteredProveedores.length}</span>
          </div>
        </div>
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleCloseModal}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Proveedor *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Escriba el nombre del proveedor"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código de Proveedor *
                </label>
                <input
                  type="text"
                  value={formData.codigo_proveedor}
                  onChange={(e) => setFormData({ ...formData, codigo_proveedor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: PRV-001"
                  required
                />
              </div>

              {editingProveedor && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    Al cambiar el nombre, se actualizarán todos los productos asociados a este proveedor.
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingProveedor ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal (Styled like User Delete) */}
      {showStatusModal && selectedProveedor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => {
          setShowStatusModal(false);
          setSelectedProveedor(null);
        }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={`${selectedProveedor.estado ? 'bg-red-600' : 'bg-green-600'} px-6 py-4`}>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {selectedProveedor.estado ? (
                  <>
                    <NoSymbolIcon className="w-6 h-6" />
                    Desactivar Proveedor
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-6 h-6" />
                    Activar Proveedor
                  </>
                )}
              </h2>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-gray-800 mb-4 text-center text-lg">
                ¿Estás seguro de {selectedProveedor.estado ? 'desactivar' : 'activar'} al proveedor <span className="font-bold text-gray-900">{selectedProveedor.nombre}</span>?
              </p>

              <div className={`${selectedProveedor.estado ? 'bg-yellow-50 border-yellow-400 text-yellow-800' : 'bg-blue-50 border-blue-400 text-blue-800'} border-l-4 p-4 mb-4 rounded`}>
                <div className="flex items-start">
                  <div className="ml-1">
                    <p className="font-bold mb-2">Advertencia de Impacto:</p>
                    <p className="text-sm">
                      Esta acción afectará a TODOS los productos asociados a este proveedor.
                    </p>
                    <ul className="list-disc ml-5 mt-2 text-sm">
                      {selectedProveedor.estado ? (
                        <>
                          <li>{selectedProveedor.totalProductos} productos se marcarán como <strong>Inactivos</strong>.</li>
                          <li>Dejarán de estar visibles para venta.</li>
                        </>
                      ) : (
                        <>
                          <li>{selectedProveedor.totalProductos} productos se marcarán como <strong>Activos</strong>.</li>
                          <li>Volverán a estar visibles para venta.</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedProveedor(null);
                }}
                className="px-5 py-2.5 border-2 border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-100 hover:border-gray-400 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
                disabled={statusLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmStatusChange}
                disabled={statusLoading}
                className={`px-5 py-2.5 text-white font-medium rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all ${selectedProveedor.estado
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  }`}
              >
                {statusLoading ? 'Procesando...' : (selectedProveedor.estado ? 'Desactivar' : 'Activar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProveedoresPage;